/* eslint-disable react/jsx-indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { rrulestr } from 'rrule';
import PropTypes from 'prop-types';
import Moment from 'moment';
import RRuleGenerator from 'react-rrule-generator';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EventRepeat from '@mui/icons-material/EventRepeat';
import Link from '@mui/material/Link';
import AccessTime from '@mui/icons-material/AccessTime';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Today from '@mui/icons-material/Today';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { Schedule } from '@mui/icons-material';
import { Switch } from '@mui/material';
import TimeSelect from '../../../../TimeSelect';
import TimezoneSelect from '../../../../TimezoneSelect';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const SchedulingSection = ({
  validation,
  schedule,
  event,
  startDate,
  endDate,
  eventTemplate,
  duration,
  rrule,
  setStartDate,
  setEndDate,
  setRrule,
  setDuration,
  setAdvanceNotice,
  timezone,
  setTimezone,
  disabledForBeingInPast,
  defaultOpenRepeat,
  timezoneWarning,
}) => {
  const [openRrule, setOpenRrule] = React.useState(false);
  const [changeTimezone, setChangeTimezone] = React.useState(false);
  const [openTimezoneWarning, setOpenTimezoneWarning] = React.useState(false);

  const scheduleStartDate = new Date((schedule && schedule && schedule.parentSchedule && schedule.parentSchedule.startDate)
    || (schedule && schedule.startDate) || startDate);

  React.useEffect(() => {
    setOpenRrule(!!defaultOpenRepeat);
    setChangeTimezone(false);
    setOpenTimezoneWarning(false);
  }, [schedule, defaultOpenRepeat]);

  if (!eventTemplate) return <div />;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ListItem dense>
        <ListItemIcon><Today /></ListItemIcon>
        <ListItemText>
          <Grid container spacing={1} alignItems="center" justifyContent="space-between">
            <Grid item>
              <DatePicker
                label={(event || (endDate !== null && startDate === endDate))
                  ? 'Date'
                  : 'Start Date'}
                value={startDate}
                disabled={disabledForBeingInPast}
                onChange={(newValue) => {
                  if (endDate !== null && startDate.getTime() === endDate.getTime()) {
                    // we are on single occurance so keep end date same
                    setEndDate(newValue.toDate());
                  }
                  setStartDate(newValue.toDate());
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    disabled={disabledForBeingInPast}
                    helperText={validation.startDate}
                    error={!!validation.startDate}
                    size="small"
                    sx={{
                      maxWidth: changeTimezone
                        ? 180
                        : 200,
                      mr: 1,
                    }}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {
                  changeTimezone
                    ? (
                      <TimezoneSelect
                        label="Timezone"
                        value={timezone}
                        onChange={(e, value) => setTimezone(value)}
                        sx={{
                          margin: 0,
                          minWidth: timezoneWarning
                            ? 150
                            : 190,
                        }}
                        disabled={disabledForBeingInPast}
                      />
                    )
                    : (
                      <Tooltip title="Change timezone">
                        <Link
                          component="button"
                          variant="body2"
                          color="secondary"
                          sx={{
                            maxWidth: timezoneWarning
                              ? 140
                              : 190,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                          underline="hover"
                          onClick={() => setChangeTimezone(true)}
                          disabled={disabledForBeingInPast}
                        >
                          {Moment.tz(timezone).zoneAbbr()} ({timezone})
                        </Link>
                      </Tooltip>
                    )
                }

                {
                  timezoneWarning && (
                    <IconButton
                      sx={{
                        p: 0,
                        ml: 1,
                      }}
                      onClick={() => setOpenTimezoneWarning(!openTimezoneWarning)}
                    >
                      <Schedule color="warning" />
                    </IconButton>
                  )
                }
              </Box>
            </Grid>
            {
              timezoneWarning && openTimezoneWarning && (
                <Grid item>
                  <Collapse in={openTimezoneWarning} timeout="auto" unmountOnExit>
                    {timezoneWarning}
                  </Collapse>
                </Grid>
              )
            }
          </Grid>
        </ListItemText>
      </ListItem>

      <ListItem
        dense
        sx={{
          pt: 0,
          pb: 0,
        }}
      >
        <ListItemIcon><AccessTime /></ListItemIcon>
        <ListItemText
          sx={{
            mt: 0,
            mb: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Link
              component="button"
              variant="body2"
              color="secondary"
              sx={{ mt: '8px' }}
              underline="hover"
              onClick={() => {
                if (!duration) {
                  setDuration((eventTemplate && eventTemplate.eventDataDefaultValues
                    && eventTemplate.eventDataDefaultValues.defaultDuration
                    && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000)
                  // default to a better time, noon
                  const newStart = Moment(startDate).set('hour', Moment().hours() + 1).toDate();
                  if (startDate.getTime() === (endDate
                    ? endDate.getTime()
                    : 1)) {
                    // keep end date same if it was/if single occurence
                    setEndDate(newStart);
                  }
                  setStartDate(newStart);
                } else {
                  setDuration(0);
                  // All day events default create tasks at 12a
                  const midnightDayOf = Moment(startDate).startOf('day').toDate();
                  if (startDate.getTime() === endDate.getTime()) {
                    // keep end date same if it was/if single occurence
                    setEndDate(midnightDayOf);
                  }
                  setStartDate(midnightDayOf);
                  setAdvanceNotice(0);
                }
              }}
            >
              {disabledForBeingInPast
                ? 'No time specified for this task'
                : 'All Day'}
            </Link>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <Switch
                checked={!duration}
                onChange={() => {
                  if (!duration) {
                    setDuration((eventTemplate && eventTemplate.eventDataDefaultValues
                      && eventTemplate.eventDataDefaultValues.defaultDuration
                      && parseInt(eventTemplate.eventDataDefaultValues.defaultDuration, 10)) || 1800000)
                    // default to a better time, noon
                    const newStart = Moment(startDate).set('hour', Moment().hours() + 1).toDate();
                    if (startDate.getTime() === (endDate
                      ? endDate.getTime()
                      : 1)) {
                      // keep end date same if it was/if single occurence
                      setEndDate(newStart);
                    }
                    setStartDate(newStart);
                  } else {
                    setDuration(0);
                    // All day events default create tasks at 12a
                    const midnightDayOf = Moment(startDate).startOf('day').toDate();
                    if (startDate.getTime() === endDate.getTime()) {
                      // keep end date same if it was/if single occurence
                      setEndDate(midnightDayOf);
                    }
                    setStartDate(midnightDayOf);
                    setAdvanceNotice(0);
                  }
                }}
                inputProps={{ 'aria-label': 'controlled' }}
              />
              {duration
                ? (
                  <Grid container spacing={1} justifyContent="flex-end">
                    <Grid item>
                      <TimeSelect
                        label="Start Time"
                        value={startDate}
                        onChange={(e) => {
                          const newValue = new Date(e.target.value);
                          // invalid date check
                          if (endDate !== null && startDate.getTime() === endDate.getTime()) {
                            // we are on single occurance so keep end date same
                            setEndDate(newValue);
                          }
                          setStartDate(newValue);
                        }}
                        sx={{
                          minWidth: 120,
                          mr: 1,
                        }}
                        disabled={disabledForBeingInPast}
                      />
                    </Grid>
                    <Grid item>
                      <TimeSelect
                        label="End Time"
                        value={new Date(startDate.getTime() + duration)}
                        onChange={(e) => {
                          const newValue = new Date(e.target.value);
                          if (newValue.getTime() - startDate.getTime() !== 0) {
                            setDuration(newValue.getTime() - startDate.getTime());
                          }
                        }}
                        sx={{ minWidth: 120 }}
                        disabled={disabledForBeingInPast}
                      />
                    </Grid>
                  </Grid>
                )
                : <Box />}
            </Box>
          </Box>
        </ListItemText>
      </ListItem>

      <ListItem
        button={!disabledForBeingInPast && !(endDate !== null && scheduleStartDate.getTime() === endDate.getTime())}
        onClick={() => { if (!(endDate !== null && scheduleStartDate.getTime() === endDate.getTime())) { setOpenRrule(!openRrule); } }}
        dense
      >
        <ListItemIcon><EventRepeat /></ListItemIcon>
        <ListItemText>
          {
            (endDate !== null && startDate.getTime() === endDate.getTime())
              ? (
                <Tooltip
                  title="Select to repeat this scheduled task"
                >
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => {
                      setEndDate(Moment(scheduleStartDate).add(7, 'days').toDate());
                      setRrule('RRULE:FREQ=DAILY;INTERVAL=1');
                      setOpenRrule(true);
                    }}
                  >
                    {disabledForBeingInPast
                      ? 'This task does not repeat'
                      : 'Repeat Task'}
                  </Link>
                </Tooltip>
              )
              : (
                <Typography color="primary" component="span" variant="subtitle1" sx={{ textWrap: 'auto' }}>{`Task Repeats: ${capitalize(rrulestr(rrule).toText())} ${endDate
                  ? `ending ${Moment(endDate).format('ll')}`
                  : ''}`}
                </Typography>
              )
          }

          {
            ((!endDate || endDate.getTime() !== startDate.getTime())) && (
              <Box sx={{ mt: 1 }}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  minDate={startDate}
                  onChange={(newValue) => {
                    if (newValue && newValue.toDate) {
                      setEndDate(newValue.toDate());
                    } else {
                      setEndDate(null);
                    }
                  }}
                  onClick={(e) => { e.stopPropagation(); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onClick={(e) => { e.stopPropagation(); }}
                      helperText={validation.endDate}
                      error={!!validation.endDate}
                      disabled={!endDate}
                      size="small"
                      sx={{
                        maxWidth: 200,
                        mr: 1,
                      }}
                    />
                  )}
                />
              </Box>
            )
          }
        </ListItemText>
        {
          (!disabledForBeingInPast && startDate.getTime() !== (endDate
            ? endDate.getTime()
            : 0)) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title="Remove repeating of this task">
                <Link
                  component="button"
                  variant="body2"
                  color="secondary"
                  sx={{ ml: 1 }}
                  underline="hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setEndDate(startDate);
                    setOpenRrule(false);
                  }}
                >
                  Remove Repeat
                </Link>
              </Tooltip>
              {openRrule
                ? <IconButton onClick={(e) => { e.stopPropagation(); setOpenRrule(false); }}><ExpandLess /></IconButton>
                : <IconButton onClick={(e) => { e.stopPropagation(); setOpenRrule(true); }}><ExpandMore /></IconButton>}
            </Box>
          )
        }
      </ListItem>
      <Collapse in={(!disabledForBeingInPast && openRrule) || !!validation.endDate} timeout="auto" unmountOnExit>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box
              sx={{ m: 2 }}
            >
              <RRuleGenerator
                value={rrule}
                onChange={setRrule}
                config={{
                  repeat: ['Monthly', 'Weekly', 'Daily', 'Hourly'],
                  monthly: 'on the',
                  end: ['Never', 'On date'],
                  weekStartsOnSunday: true,
                  hideStart: true,
                  hideEnd: true,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </LocalizationProvider>
  );
};

SchedulingSection.propTypes = {
  validation: PropTypes.object.isRequired,
  schedule: PropTypes.object,
  event: PropTypes.object,
  startDate: PropTypes.any.isRequired,
  endDate: PropTypes.any,
  duration: PropTypes.any.isRequired,
  eventTemplate: PropTypes.object,
  rrule: PropTypes.any.isRequired,
  setStartDate: PropTypes.func,
  setEndDate: PropTypes.func,
  setRrule: PropTypes.func,
  setAdvanceNotice: PropTypes.func,
  setDuration: PropTypes.func,
  setTimezone: PropTypes.func,
  timezone: PropTypes.any.isRequired,
  disabledForBeingInPast: PropTypes.bool,
  defaultOpenRepeat: PropTypes.bool,
  timezoneWarning: PropTypes.any,
};

SchedulingSection.defaultProps = {
  schedule: null,
  event: null,
  eventTemplate: {},
  endDate: null,
  disabledForBeingInPast: false,
  setAdvanceNotice: () => { },
  setDuration: () => { },
  setTimezone: () => { },
  setStartDate: () => { },
  setEndDate: () => { },
  setRrule: () => { },
  defaultOpenRepeat: false,
  timezoneWarning: null,
};

export default SchedulingSection;
